import { useEffect, useMemo, useRef, useState } from 'react'

import { debounce } from 'lodash'

import { toast } from '@webapp/components/Notification'
import { type ApiError } from '@webapp/core/interfaces/api/ApiError'
import { useProcurifyIntl } from '@webapp/hooks'
import { HttpError } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { useMutateExternalPONumbering } from '@webapp/modules/purchasing/features/set-external-po-numbering/models'
import messages from './messages'

const SAVE_DEBOUNCE_MS = 500

export interface IUseExternalPONumberingTogglePresenterProps {
  isEnabled: boolean
}

// The shared preferences repository throws raw HttpError (message
// "HTTP Error <status>"), so getMessageFromError would surface the generic
// copy — read the API `errors.detail.message` when present instead.
const getApiDetailMessage = (error: unknown): string | undefined =>
  error instanceof HttpError
    ? (error as HttpError<ApiError>).response?.errors?.detail?.message
    : undefined

/**
 * Owns the toggle's optimistic value, the 500ms-debounced save (BHV-005) and
 * the revert-to-prior-state-on-failure behaviour (BHV-006). The current saved
 * value arrives as a prop from the Feature Set (feature-level CQS — this
 * command feature never queries).
 */
export const useExternalPONumberingTogglePresenter = ({
  isEnabled,
}: IUseExternalPONumberingTogglePresenterProps) => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateExternalPONumbering()

  // Optimistic flip — displayed immediately, persisted after the debounce
  const [value, setValue] = useState(isEnabled)

  // Last persisted value, used to revert the optimistic flip on failure
  const savedValueRef = useRef(isEnabled)

  useEffect(() => {
    savedValueRef.current = isEnabled
    setValue(isEnabled)
  }, [isEnabled])

  const intlRef = useRef(intl)
  intlRef.current = intl

  const mutateAsyncRef = useRef(mutateAsync)
  mutateAsyncRef.current = mutateAsync

  // Stable for the hook's lifetime (empty deps) so rapid flips coalesce into
  // one trailing save (BHV-005); intl/mutate are read through refs
  const debouncedSave = useMemo(
    () =>
      debounce(async (next: boolean) => {
        try {
          await mutateAsyncRef.current(next)
          savedValueRef.current = next

          toast.success({
            title: intlRef.current.formatMessage(messages.successTitle),
            children: intlRef.current.formatMessage(messages.successMessage),
          })
        } catch (error) {
          // Revert to the prior saved state BEFORE showing the error toast
          // (BHV-006)
          setValue(savedValueRef.current)

          toast.error({
            title: intlRef.current.formatMessage(messages.errorTitle),
            children:
              getApiDetailMessage(error) ??
              intlRef.current.formatMessage(messages.errorMessage),
          })
        }
      }, SAVE_DEBOUNCE_MS),
    []
  )

  useEffect(
    () => () => {
      debouncedSave.cancel()
    },
    [debouncedSave]
  )

  const onToggle = (next: boolean) => {
    setValue(next)
    void debouncedSave(next)
  }

  return {
    states: {
      value,
      isSaving: isLoading,
    },
    methods: {
      onToggle,
    },
  }
}
